<template>
  <section class="projects-section modern-projects-section">
    <div class="section-container">
      <div class="section-header">
        <h2 class="section-title modern-title">Featured Projects</h2>
        <p class="section-description modern-description">{{ description }}</p>
        <div class="section-divider"></div>
      </div>

      <v-row class="projects-grid">
        <v-col
          v-for="(project, index) in projects"
          :key="project.name"
          cols="12"
          sm="6"
          md="4"
        >
          <v-card
            class="project-card modern-project-card"
            :class="{
              'clickable': project.link,
              'is-hovered': project.isHovered,
              'has-link': project.link
            }"
            @click="project.link && openLink(project.link)"
            @mouseenter="handleCardHover(project, true)"
            @mouseleave="handleCardHover(project, false)"
            elevation="0"
          >
            <!-- Enhanced Image Container -->
            <div class="project-image-container modern-image-container">
              <img
                :src="project.img"
                :alt="project.name"
                class="project-image modern-image"
              >

              <!-- Modern Overlay with Enhanced Interactions -->
              <div class="project-overlay modern-overlay">
                <div class="overlay-content">
                  <div class="overlay-icons">
                    <v-btn
                      v-if="project.link"
                      icon
                      large
                      color="white"
                      class="overlay-btn primary-btn"
                      @click.stop="openLink(project.link)"
                    >
                      <v-icon size="28">mdi-open-in-new</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      large
                      color="white"
                      class="overlay-btn secondary-btn"
                      @click.stop="showProjectDetails(project)"
                    >
                      <v-icon size="28">mdi-information-outline</v-icon>
                    </v-btn>
                  </div>

                  <div class="overlay-text">
                    <h3 class="overlay-title">{{ project.name }}</h3>
                    <p class="overlay-description">{{ project.shortDescription || project.description.substring(0, 80) + '...' }}</p>
                  </div>
                </div>
              </div>

              <!-- Gradient Overlay -->
              <div class="gradient-overlay"></div>

              <!-- Status Badge -->
              <div
                v-if="project.status"
                class="status-badge modern-status-badge"
                :style="getStatusBadgeStyle(project.status)"
              >
                <v-icon size="14" color="white">{{ getStatusIcon(project.status) }}</v-icon>
                <span>{{ project.status }}</span>
              </div>

              <!-- Technology Preview -->
              <div class="tech-preview">
                <v-chip
                  v-for="(tech, techIndex) in project.technologies.slice(0, 2)"
                  :key="techIndex"
                  x-small
                  class="tech-preview-chip"
                  :style="getTechChipStyle(techIndex)"
                >
                  {{ tech }}
                </v-chip>
                <span v-if="project.technologies.length > 2" class="tech-more">
                  +{{ project.technologies.length - 2 }}
                </span>
              </div>
            </div>

            <!-- Enhanced Card Content -->
            <div class="project-content modern-content">
              <v-card-title class="project-title modern-card-title">
                <span class="title-text">{{ project.name }}</span>
                <v-chip
                  v-if="project.status"
                  x-small
                  :color="getStatusColor(project.status)"
                  class="status-chip"
                  text-color="white"
                >
                  {{ project.status }}
                </v-chip>
              </v-card-title>

              <v-card-text class="project-description modern-card-description">
                <p class="description-text">{{ project.description }}</p>

                <!-- Project Metrics (if available) -->
                <div v-if="project.metrics" class="project-metrics">
                  <div
                    v-for="(metric, metricIndex) in project.metrics"
                    :key="metricIndex"
                    class="metric-item"
                  >
                    <v-icon small :color="metric.color" class="metric-icon">
                      {{ metric.icon }}
                    </v-icon>
                    <span class="metric-text">{{ metric.text }}</span>
                  </div>
                </div>

                <!-- Enhanced Tech Stack -->
                <div class="project-tech-stack modern-tech-stack">
                  <h4 class="tech-stack-title">Technologies:</h4>
                  <div class="tech-chips-container">
                    <v-chip
                      v-for="(tech, techIndex) in project.technologies"
                      :key="techIndex"
                      small
                      outlined
                      class="tech-chip modern-tech-chip"
                      :style="getTechChipStyle(techIndex)"
                    >
                      <v-icon left size="14" :color="getTechColor(tech)">
                        {{ getTechIcon(tech) }}
                      </v-icon>
                      {{ tech }}
                    </v-chip>
                  </div>
                </div>

                <!-- Project Features -->
                <div v-if="project.features" class="project-features">
                  <h4 class="features-title">Key Features:</h4>
                  <ul class="features-list">
                    <li
                      v-for="(feature, featureIndex) in project.features.slice(0, 3)"
                      :key="featureIndex"
                      class="feature-item"
                    >
                      <v-icon small color="primary" class="feature-icon">mdi-check-circle</v-icon>
                      {{ feature }}
                    </li>
                  </ul>
                </div>
              </v-card-text>

              <!-- Modern Action Bar -->
              <div class="project-actions modern-actions">
                <v-btn
                  v-if="project.link"
                  small
                  color="primary"
                  class="action-btn primary-action"
                  @click.stop="openLink(project.link)"
                >
                  <v-icon left size="16">mdi-launch</v-icon>
                  View Live
                </v-btn>

                <v-btn
                  v-if="project.github"
                  small
                  outlined
                  color="primary"
                  class="action-btn secondary-action"
                  @click.stop="openLink(project.github)"
                >
                  <v-icon left size="16">mdi-github</v-icon>
                  Code
                </v-btn>

                <v-btn
                  small
                  text
                  color="primary"
                  class="action-btn details-action"
                  @click.stop="showProjectDetails(project)"
                >
                  <v-icon left size="16">mdi-information</v-icon>
                  Details
                </v-btn>
              </div>

              <!-- Project Progress Indicator -->
              <div class="project-progress">
                <div class="progress-bar" :style="getProgressStyle(project.status)"></div>
                <div class="progress-label">{{ getProgressLabel(project.status) }}</div>
              </div>
            </div>

            <!-- Card Glow Effect -->
            <div class="card-glow" :style="getCardGlowStyle(index)"></div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Enhanced View All Projects Button -->
      <div class="view-all-projects modern-view-all">
        <v-btn
          color="primary"
          outlined
          rounded
          large
          class="enhanced-btn modern-view-btn"
          @click="$emit('viewAllProjects')"
        >
          <v-icon left size="20">mdi-briefcase-search</v-icon>
          Explore All Projects
          <v-icon right size="20">mdi-arrow-right</v-icon>
        </v-btn>
      </div>

      <!-- Projects Statistics -->
      <div class="projects-stats">
        <div class="stats-container">
          <div class="stat-item">
            <div class="stat-number">{{ getProjectCount('live') }}</div>
            <div class="stat-label">Live Projects</div>
          </div>
          <div class="stat-item">
            <div class="stat-number">{{ getProjectCount('development') }}</div>
            <div class="stat-label">In Development</div>
          </div>
          <div class="stat-item">
            <div class="stat-number">{{ getTotalTechnologies() }}</div>
            <div class="stat-label">Technologies</div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "FeaturedProjectsSection",
  props: {
    projects: {
      type: Array,
      required: true,
      default: () => []
    },
    description: {
      type: String,
      default: "Showcasing my latest work and technical expertise"
    }
  },
  data() {
    return {
      colors: ['#4361ee', '#f72585', '#4cc9f0', '#7209b7', '#f77f00', '#06ffa5'],
    };
  },
  methods: {
    handleCardHover(project, isHovered) {
      this.$set(project, 'isHovered', isHovered);
    },

    openLink(url) {
      if (url) {
        window.open(url, "_blank");
      }
    },

    showProjectDetails(project) {
      // Emit event to show project details in a modal
      this.$emit('showProjectDetails', project);
    },

    getStatusColor(status) {
      switch (status.toLowerCase()) {
        case 'live':
          return 'success';
        case 'in development':
          return 'warning';
        case 'planned':
          return 'info';
        case 'completed':
          return 'primary';
        default:
          return 'grey';
      }
    },

    getStatusIcon(status) {
      switch (status.toLowerCase()) {
        case 'live':
          return 'mdi-check-circle';
        case 'in development':
          return 'mdi-progress-clock';
        case 'planned':
          return 'mdi-calendar-clock';
        case 'completed':
          return 'mdi-check-all';
        default:
          return 'mdi-help-circle';
      }
    },

    getStatusBadgeStyle(status) {
      const color = this.getStatusColor(status);
      const colorMap = {
        'success': '#4caf50',
        'warning': '#ff9800',
        'info': '#2196f3',
        'primary': '#4361ee',
        'grey': '#9e9e9e'
      };

      return {
        backgroundColor: colorMap[color] || colorMap.grey,
        boxShadow: `0 4px 12px ${colorMap[color] || colorMap.grey}40`
      };
    },

    getTechColor(tech) {
      const techColors = {
        'Vue.js': '#42B883',
        'React': '#61DAFB',
        'Node.js': '#68A063',
        'JavaScript': '#F0DB4F',
        'TypeScript': '#3178C6',
        'Python': '#3572A5',
        'MongoDB': '#4DB33D',
        'PostgreSQL': '#336791',
        'Firebase': '#FFA000',
        'Express': '#000000',
        'Docker': '#2496ED',
        'AWS': '#232F3E'
      };
      return techColors[tech] || '#666';
    },

    getTechIcon(tech) {
      const techIcons = {
        'Vue.js': 'mdi-vuejs',
        'React': 'mdi-react',
        'Node.js': 'mdi-nodejs',
        'JavaScript': 'mdi-language-javascript',
        'TypeScript': 'mdi-language-typescript',
        'Python': 'mdi-language-python',
        'MongoDB': 'mdi-database',
        'PostgreSQL': 'mdi-database',
        'Firebase': 'mdi-firebase',
        'Express': 'mdi-server',
        'Docker': 'mdi-docker',
        'AWS': 'mdi-aws'
      };
      return techIcons[tech] || 'mdi-code-tags';
    },

    getTechChipStyle(index) {
      const color = this.colors[index % this.colors.length];
      return {
        borderColor: color,
        color: color
      };
    },

    getCardGlowStyle(index) {
      const color = this.colors[index % this.colors.length];
      return {
        background: `radial-gradient(circle at center, ${color}15, transparent 70%)`,
        opacity: 0
      };
    },

    getProgressStyle(status) {
      let width = '0%';
      let color = '#4caf50';

      switch (status?.toLowerCase()) {
        case 'live':
          width = '100%';
          color = '#4caf50';
          break;
        case 'in development':
          width = '70%';
          color = '#ff9800';
          break;
        case 'planned':
          width = '20%';
          color = '#2196f3';
          break;
        default:
          width = '50%';
          color = '#9e9e9e';
      }

      return {
        width,
        backgroundColor: color
      };
    },

    getProgressLabel(status) {
      switch (status?.toLowerCase()) {
        case 'live':
          return 'Production Ready';
        case 'in development':
          return 'Active Development';
        case 'planned':
          return 'Planning Phase';
        default:
          return 'In Progress';
      }
    },

    getProjectCount(type) {
      return this.projects.filter(project => {
        const status = project.status?.toLowerCase();
        if (type === 'live') return status === 'live';
        if (type === 'development') return status === 'in development';
        return false;
      }).length;
    },

    getTotalTechnologies() {
      const allTechs = new Set();
      this.projects.forEach(project => {
        project.technologies?.forEach(tech => allTechs.add(tech));
      });
      return allTechs.size;
    }
  },

  mounted() {
    // Initialize project states
    this.projects.forEach(project => {
      this.$set(project, 'isHovered', false);
    });
  }
};
</script>

<style lang="scss" scoped>
@import "@/styles/variables.scss";

.projects-section {
  padding: 4rem 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background:
      radial-gradient(circle at 30% 20%, rgba(67, 97, 238, 0.08), transparent 50%),
      radial-gradient(circle at 70% 80%, rgba(247, 37, 133, 0.06), transparent 50%);
    pointer-events: none;
  }
}

.section-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
}

.section-header {
  text-align: center;
  margin-bottom: 4rem;
}

.modern-title {
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, $primary, $accent);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-family: 'Poppins', sans-serif;
  text-shadow: 0 4px 20px rgba(67, 97, 238, 0.3);

  .theme--light & {
    text-shadow: 0 2px 10px rgba(67, 97, 238, 0.2);
  }

  @media (max-width: $breakpoint-xs) {
    font-size: 2.2rem;
  }
}

.modern-description {
  max-width: 600px;
  margin: 0 auto 2rem;
  font-size: 1.2rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 400;

  .theme--light & {
    color: rgba(73, 80, 87, 0.8);
  }
}

.section-divider {
  width: 120px;
  height: 4px;
  background: linear-gradient(90deg, $primary, $accent, #f72585);
  margin: 0 auto;
  border-radius: 2px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(90deg, $primary, $accent, #f72585);
    border-radius: 4px;
    opacity: 0.3;
    filter: blur(4px);
  }
}

.projects-grid {
  margin-bottom: 3rem;
}

// Modern Project Card
.modern-project-card {
  height: 100%;
  border-radius: 24px;
  overflow: hidden;
  position: relative;
  transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
  background: rgba(255, 255, 255, 0.08) !important;
  backdrop-filter: blur(25px);
  border: 1px solid rgba(255, 255, 255, 0.12);

  .theme--light & {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1px solid rgba(67, 97, 238, 0.08);
    box-shadow: 0 8px 32px rgba(67, 97, 238, 0.08);
  }

  &.clickable {
    cursor: pointer;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, transparent, rgba(255,255,255,0.1), transparent);
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: 1;
  }

  &.is-hovered {
    transform: translateY(-20px) scale(1.03);
    border-color: rgba(67, 97, 238, 0.4);
    box-shadow:
      0 30px 60px rgba(0, 0, 0, 0.4),
      0 0 50px rgba(67, 97, 238, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.1);

    .theme--light & {
      box-shadow:
        0 30px 60px rgba(67, 97, 238, 0.15),
        0 0 50px rgba(67, 97, 238, 0.1);
      border-color: rgba(67, 97, 238, 0.2);
    }

    &::before {
      opacity: 1;
    }

    .card-glow {
      opacity: 1 !important;
      transform: scale(1.2);
    }

    .modern-overlay {
      opacity: 1;
    }

    .modern-image {
      transform: scale(1.15);
    }

    .tech-preview {
      opacity: 0;
      transform: translateY(-20px);
    }

    .status-badge {
      transform: scale(1.1);
    }
  }
}

// Modern Image Container
.modern-image-container {
  position: relative;
  height: 240px;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(67, 97, 238, 0.1), transparent);
    z-index: 2;
  }
}

.modern-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.6s cubic-bezier(0.25, 0.8, 0.25, 1);
  filter: brightness(0.9) contrast(1.1);

  .theme--light & {
    filter: brightness(1) contrast(1);
  }
}

.modern-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.7), rgba(67, 97, 238, 0.4));
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  z-index: 3;
}

.overlay-content {
  text-align: center;
  transform: translateY(20px);
  transition: transform 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);

  .modern-project-card.is-hovered & {
    transform: translateY(0);
  }
}

.overlay-icons {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;

  .overlay-btn {
    backdrop-filter: blur(10px);
    background: rgba(255, 255, 255, 0.2) !important;
    transition: all 0.2s ease;

    &:hover {
      transform: scale(1.1);
      background: rgba(255, 255, 255, 0.3) !important;
    }

    &.primary-btn {
      border: 2px solid rgba(76, 175, 80, 0.5);
    }

    &.secondary-btn {
      border: 2px solid rgba(33, 150, 243, 0.5);
    }
  }
}

.overlay-title {
  color: white;
  font-size: 1.3rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
}

.overlay-description {
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.95rem;
  line-height: 1.4;
  margin: 0;
}

.gradient-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 50%;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.6));
  z-index: 2;
}

.status-badge {
  position: absolute;
  top: 1rem;
  left: 1rem;
  padding: 0.3rem 0.8rem;
  border-radius: 16px;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: white;
  backdrop-filter: blur(10px);
  z-index: 4;
  transition: all 0.3s ease;

  span {
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}

.tech-preview {
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  transition: all 0.3s ease;
  z-index: 4;
}

.tech-preview-chip {
  background: rgba(255, 255, 255, 0.9) !important;
  color: rgba(33, 37, 41, 0.8) !important;
  font-weight: 600;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.tech-more {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  font-weight: 600;
  padding: 0.2rem 0.5rem;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  backdrop-filter: blur(10px);
}

// Modern Card Content
.modern-content {
  padding: 0;
}

.modern-card-title {
  padding: 1.5rem 2rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.title-text {
  font-weight: 700;
  font-size: 1.3rem;
  color: rgba(255, 255, 255, 0.95);
  font-family: 'Poppins', sans-serif;
  line-height: 1.3;

  .theme--light & {
    color: rgba(33, 37, 41, 0.9);
  }
}

.status-chip {
  font-size: 0.7rem;
  font-weight: 600;
  border-radius: 12px;
  height: 22px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.modern-card-description {
  padding: 0 2rem 1rem;
}

.description-text {
  font-size: 1rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 1.5rem;

  .theme--light & {
    color: rgba(73, 80, 87, 0.8);
  }
}

.project-metrics {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
}

.metric-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.metric-text {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);

  .theme--light & {
    color: rgba(73, 80, 87, 0.7);
  }
}

.modern-tech-stack {
  margin-bottom: 1.5rem;
}

.tech-stack-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 0.8rem;

  .theme--light & {
    color: rgba(33, 37, 41, 0.8);
  }
}

.tech-chips-container {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.modern-tech-chip {
  font-size: 0.75rem;
  height: 26px;
  border-radius: 13px;
  transition: all 0.2s ease;
  background: rgba(255, 255, 255, 0.05);

  .theme--light & {
    background: rgba(255, 255, 255, 0.8);
  }

  &:hover {
    transform: scale(1.05);
  }

  .v-icon {
    margin-left: -4px !important;
  }
}

.project-features {
  margin-bottom: 1rem;
}

.features-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 0.8rem;

  .theme--light & {
    color: rgba(33, 37, 41, 0.8);
  }
}

.features-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.4rem;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);

  .theme--light & {
    color: rgba(73, 80, 87, 0.8);
  }
}

.modern-actions {
  padding: 1rem 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  gap: 0.5rem;
  justify-content: space-between;

  .theme--light & {
    border-color: rgba(67, 97, 238, 0.1);
  }
}

.action-btn {
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: none;
  flex: 1;
  min-width: 0;

  &.primary-action {
    background: linear-gradient(135deg, $primary, $accent) !important;
    box-shadow: 0 4px 16px rgba(67, 97, 238, 0.3);
  }

  &.secondary-action {
    border-color: rgba(255, 255, 255, 0.3);

    .theme--light & {
      border-color: rgba(67, 97, 238, 0.3);
    }
  }
}

.project-progress {
  padding: 0 2rem 1.5rem;
}

.progress-bar {
  height: 4px;
  border-radius: 2px;
  transition: width 1s ease;
  margin-bottom: 0.5rem;
}

.progress-label {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
  text-align: center;

  .theme--light & {
    color: rgba(73, 80, 87, 0.7);
  }
}

.card-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 120%;
  height: 120%;
  transition: all 0.5s ease;
  pointer-events: none;
  z-index: -1;
}

// Enhanced View All Projects
.modern-view-all {
  text-align: center;
  margin-bottom: 3rem;
}

.modern-view-btn {
  background: rgba(255, 255, 255, 0.1) !important;
  backdrop-filter: blur(20px);
  border: 2px solid rgba(67, 97, 238, 0.3);
  font-weight: 600;
  padding: 0 2rem;

  .theme--light & {
    background: rgba(67, 97, 238, 0.05) !important;
    border-color: rgba(67, 97, 238, 0.2);
  }

  &:hover {
    background: rgba(67, 97, 238, 0.2) !important;
    border-color: rgba(67, 97, 238, 0.5);
    transform: translateY(-4px);
    box-shadow: 0 12px 30px rgba(67, 97, 238, 0.2);
  }
}

// Projects Statistics
.projects-stats {
  text-align: center;
  margin-top: 2rem;
}

.stats-container {
  display: flex;
  justify-content: center;
  gap: 3rem;
}

.stat-item {
  text-align: center;
}

.stat-number {
  font-size: 2.5rem;
  font-weight: 800;
  color: $primary;
  line-height: 1;
  margin-bottom: 0.5rem;
  font-family: 'Poppins', sans-serif;
}

.stat-label {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;

  .theme--light & {
    color: rgba(73, 80, 87, 0.7);
  }
}

// Responsive Design
@media (max-width: $breakpoint-md) {
  .modern-title {
    font-size: 2.5rem;
  }

  .modern-image-container {
    height: 220px;
  }

  .modern-card-title {
    padding: 1.2rem 1.5rem 0.8rem;
  }

  .modern-card-description {
    padding: 0 1.5rem 1rem;
  }

  .modern-actions {
    padding: 1rem 1.5rem;
  }

  .stats-container {
    gap: 2rem;
  }

  .stat-number {
    font-size: 2rem;
  }
}

@media (max-width: $breakpoint-xs) {
  .projects-section {
    padding: 2rem 0;
  }

  .section-container {
    padding: 0 1rem;
  }

  .modern-title {
    font-size: 2rem;
  }

  .modern-description {
    font-size: 1rem;
  }

  .modern-project-card {
    border-radius: 20px;

    &.is-hovered {
      transform: translateY(-12px) scale(1.02);
    }
  }

  .modern-image-container {
    height: 200px;
  }

  .modern-card-title {
    padding: 1rem 1.2rem 0.5rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .title-text {
    font-size: 1.1rem;
  }

  .modern-card-description {
    padding: 0 1.2rem 0.8rem;
  }

  .description-text {
    font-size: 0.9rem;
  }

  .modern-actions {
    padding: 0.8rem 1.2rem;
    flex-direction: column;
    gap: 0.8rem;
  }

  .action-btn {
    width: 100%;
  }

  .stats-container {
    flex-direction: column;
    gap: 1.5rem;
  }

  .stat-number {
    font-size: 1.8rem;
  }

  .overlay-icons {
    gap: 0.8rem;

    .overlay-btn {
      width: 44px;
      height: 44px;
    }
  }

  .tech-chips-container {
    gap: 0.3rem;
  }
}

// Dark/Light theme specific adjustments
.theme--light {
  .modern-overlay {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(67, 97, 238, 0.2));
  }

  .overlay-title {
    color: rgba(33, 37, 41, 0.9);
    text-shadow: none;
  }

  .overlay-description {
    color: rgba(73, 80, 87, 0.8);
  }

  .gradient-overlay {
    background: linear-gradient(transparent, rgba(255, 255, 255, 0.8));
  }
}
</style>